import { useContext, useEffect, useState } from "react";
import { useWarehouse } from "../Inventory/Hooks/useWarehouse";
import { STOCK_MODAL_MODES } from "../Inventory/constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Trash2 } from "lucide-react";
import { EDIT, PROCUREMENT_GRN, VIEW } from "../../routes";
import {
  AccessDenied,
  GenericTable,
  RoleValidator,
  SearchableDropdown,
  ProductCategoryFilter,
  Spinner,
} from "../../Components/Common";
import { useProcurementGRN } from "./Hooks/useProcurementGRN";
import {
  getPGRNStatusColor,
  MATERIAL_COLUMNS_PGRN,
  MATERIAL_LABELS_PGRN,
  PGRN_STATUS,
  PGRN_STATUS_VALUE_LABELS,
  PRODUCT_CONDITION,
  TOAST_MESSAGES,
} from "./constants";
import { toProperCase } from "../../Utils";
import { UserContext } from "../../Context";
import { ROLES } from "../../Constants/Strings";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { extractFileName } from "../../Utils/extractFileName";
import { downloadUtil } from "../../Utils/DownloadUtils";
import { useProcurementMRR } from "../ProcurementMRR/Hooks/useProcurementMRR";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { AdminApprovalButtons, BillingCompletionButtons, InchargeSubmitButtons, ProcurementActionButtons, SendForProcurementButton } from "./Components/SubmitButtonAprroval";

const ProcurementGRNCreate = () => {
  const [loading, setLoading] = useState(true);
  const {
    newProcurementGRN,
    fetchProcurementGRNById,
    updateProcurementGRNDetails,
    deleteInvoice,
  } = useProcurementGRN();
  const { fetchAllProcurementMRRs } = useProcurementMRR();
  const { fetchWarehouses } = useWarehouse();
  const [pmrrId, setPMRRId] = useState("");
  const [pmrr, setPMRR] = useState("");
  const [mode, setMode] = useState(STOCK_MODAL_MODES.ADD);
  const [warehouse, setWarehouse] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [formData, setFormData] = useState({
    notes: "",
  });
  const location = useLocation();
  const [finalInvoiceFile, setFinalInvoiceFile] = useState(null);
  const [finalInvoiceUrl, setFinalInvoiceUrl] = useState(null);
  const [currentStatus, setCurrentStatus] = useState();
  const [materials, setMaterials] = useState([]);
  const navigate = useNavigate();
  const param = useParams();
  const { user } = useContext(UserContext);
  const accessedRoles = [
    ROLES.INVENTORY_ADMIN,
    ROLES.INVENTORY_INCHARGE,
    ROLES.BILLING_INCHARGE,
    ROLES.PROCUREMENT_INCHARGE,
  ].includes(user?.role);
  const restrictCreatePage =
    user.role !== ROLES.INVENTORY_INCHARGE && mode === STOCK_MODAL_MODES.ADD;

  const isDisabled = mode !== STOCK_MODAL_MODES.ADD;

  const resetForm = () => {
    setFormData({ notes: ""});
    setWarehouseId("");
    setWarehouse("");
    setMaterials([]);
    setStatus("");
    setPMRRId("");
    setPMRR("");
  };

  const removeMaterial = (product) => {
    setMaterials((prev) => prev.filter((item) => item.id !== product.id));
    toast.success(`${product.product_name} removed form the list`);
  };

  const handleAddMaterial = (material) => {
    if (!material) return;
    setMaterials((prev) => {
      // Check if material with same id or name already exists
      const exists = prev.some(
        (item) =>
          item.id === material.id || item.product_name === material.product_name
      );
      if (exists) {
        toast.error(`${material.product_name} already added`);
        return prev;
      } else {
        toast.success(`${material.product_name} added`);
      }
      // skip adding duplicate
      return [...prev, { ...material, product: material.id, quantity: 1 }];
    });
  };

  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div
            className="flex flex-col"
            disabled={mode === STOCK_MODAL_MODES.VIEW}
          >
            <span className="font-semibold">{row.product_name}</span>
            <span className="text-gray-500 font-normal">
              {row.specification}
            </span>
            <span className="text-gray-800 font-normal italic">
              ({row.UOM || row.UOM_name})
            </span>
          </div>
        );
      case "product_condition":
        return (
          <div className="border-b-2 border-gray-400 py-1">
            {row.product_condition && mode === STOCK_MODAL_MODES.VIEW ? (
              // Readable text when condition already exists
              <span className="text-gray-500 text-sm font-medium">
                {toProperCase(row.product_condition) || ""}
              </span>
            ) : (
              // Dropdown only when condition is not set
              <select
                value={row.product_condition ?? ""}
                onChange={(e) =>
                  setMaterials((prev) =>
                    prev.map((m) =>
                      m.id === row.id
                        ? { ...m, product_condition: e.target.value }
                        : m
                    )
                  )
                }
                disabled={mode === STOCK_MODAL_MODES.VIEW}
                className="w-34 py-1 bg-inherit border-none focus:ring-0 text-start text-sm"
              >
                <option value="" disabled>
                  Select Condition
                </option>
                {PRODUCT_CONDITION.map((condition) => (
                  <option key={condition.value} value={condition.value}>
                    {condition.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        );

      case "comments":
        return (
          <div className="border-b-2 border-gray-400">
            <input
              type="text"
              value={row.comments ?? ""}
              placeholder="Enter note..."
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, comments: e.target.value } : m
                  )
                )
              }
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-32 py-1 bg-inherit border-none focus:ring-0  text-start"
            />
          </div>
        );
      case "quantity":
        return (
          <div className="border-b-2 border-gray-400 w-14">
            <input
              type="number"
              min="1"
              value={Number(row.quantity ?? 0)}
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, quantity: e.target.value } : m
                  )
                )
              }
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-18 py-1 bg-inherit border-none focus:ring-0 text-start"
            />
          </div>
        );
      case "actions":
        return (
          <>
            <RoleValidator allowedRoles={ROLES.INVENTORY_INCHARGE}>
              <button
                className=" hover:underline ml-2"
                onClick={() => removeMaterial(row)}
              >
                <Trash2 className="h-5 w-5 text-red-400" />
              </button>
            </RoleValidator>
          </>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const handleSubmit = async (submitType) => {
    const missingFields = [];
    if (!warehouseId) missingFields.push("Requesting warehouse");
    if (!pmrrId) missingFields.push("Procurement MRR");
    if (!formData.notes) missingFields.push("Notes");
    if (materials.length === 0) missingFields.push("at least one material");
    if ((!finalInvoiceUrl) && (currentStatus === PGRN_STATUS.PENDING_PROCUREMENT_APPROVAL))
      missingFields.push("Final Invoice file");
    if (missingFields.length > 0) {
      toast.error(`Please provide: ${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      receiving_warehouse: warehouseId,
      status: submitType,
      notes: formData.notes,
      materials,
      procurement_mrr: pmrrId,
    };
    var data = null;
    if (mode === STOCK_MODAL_MODES.ADD) {
      data = await newProcurementGRN(payload);
    } else {
      data = await updateProcurementGRNDetails(param.instanceId, payload);
      setMaterials(data.materials);
      setFormData({
        notes: data.notes,
      });
      setWarehouse(data.receiving_warehouse_name);
      setWarehouseId(data.receiving_warehouse);
      setStatus(data.status);
      setPMRRId(data.procurement_mrr);
      setPMRR(data.procurement_mrr_code);
    }
    navigate(PROCUREMENT_GRN.replace(":instanceId", data.id));
  };

  const getProcurementGRNData = async () => {
    setLoading(true);
    try {
      const data = await fetchProcurementGRNById(param.instanceId);

      if (data) {
        setFormData({
          notes: data.notes || "",
        });
        setPMRRId(data.procurement_mrr);
        setWarehouseId(data.receiving_warehouse || "");
        setWarehouse(data.receiving_warehouse_name || "");
        setMaterials(data.materials || []);
        setStatus(data.status || "");
        setPMRR(data.procurement_mrr_code);
        setCurrentStatus(data.status);
        setFinalInvoiceUrl(data.final_invoice || null);
      } else {
        resetForm();
      }
    } catch (error) {
      resetForm();
    } finally {
      setLoading(false);
    }
  };

    const handleDelete = async (fileUrl) => {
      if (!window.confirm("Delete this file?")) return;
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("final_invoice", fileUrl);
        await deleteInvoice(
          param.instanceId,
          formData,
          true // optional flag for formData
        );
        setFinalInvoiceUrl(null);
      } catch (error) {
        toast.error(
          extractErrorMessage(error) || TOAST_MESSAGES.FILE_DELETE_FAIL
        );
      } finally {
        setLoading(false);
      }
  };

    const handleDownload = async (fileUrl) => {
      setLoading(true);
      try {
        await downloadUtil(fileUrl);
        toast.success(TOAST_MESSAGES.FILE_DOWNLOAD_SUCCESS);
      } catch (error) {
        toast.error(TOAST_MESSAGES.FILE_DOWNLOAD_FAIL);
      } finally {
        setLoading(false);
      }
    };

  const handleUploadFile = async () => {
    if (!finalInvoiceFile) return;
    const formData = new FormData();
    formData.append("final_invoice", finalInvoiceFile);
    setLoading(true);
    try {
      const res = await updateProcurementGRNDetails(
        param.instanceId,
        formData,
        true // optional flag for formData
      );
      setFinalInvoiceUrl(res.final_invoice);
      setFinalInvoiceFile(null);
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FILE_UPLOAD_FAIL
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (param.instanceId) {
      getProcurementGRNData();
      if (location.pathname.includes(EDIT)) {
        setMode(STOCK_MODAL_MODES.EDIT);
      } else if (location.pathname.includes(VIEW)) {
        setMode(STOCK_MODAL_MODES.VIEW);
      }
    } else {
      setMode(STOCK_MODAL_MODES.ADD);
      setLoading(false);
    }
  }, [param.instanceId, location.pathname]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh] pt-10">
        <Spinner size={4} />
      </div>
    );

  if (!accessedRoles || restrictCreatePage) return <AccessDenied />;

  return (
    <div className="min-h-[70vh]">
      <div className="flex justify-between items-center mt-2">
        <h2 className="flex gap-2 justify-center items-center text-xl font-semibold text-gray-800 mt-2 px-3 py-2">
          <button
            type="button"
            onClick={() => navigate(PROCUREMENT_GRN)}
            className="text-primary text-center text-sm font-lg"
          >
            <ArrowLeft size={23} absoluteStrokeWidth className="text-primary" />
          </button>
          {mode === STOCK_MODAL_MODES.EDIT
            ? "Edit"
            : mode === STOCK_MODAL_MODES.VIEW
            ? ""
            : "Create"}{" "}
          Procurement Goods Receive Notes
        </h2>
      </div>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-2 md:gap-6 gap-4 px-4 py-2">
        {/* Left Side — Warehouse & Notes */}
        <div className="bg-white md:border-r-2 md:pr-8">
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            PGRN Details
          </h3>
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="w-[50%]">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                {`Requested By (Warehouse)`}{" "}
                <sub className="text-red-500">*</sub>
              </label>
              <SearchableDropdown
                fetchFunction={fetchWarehouses}
                onSelect={(val) => setWarehouseId(val.id)}
                labelKey="name"
                placeholder={warehouse ? warehouse : "Select Warehouse"}
                disabled={isDisabled}
              />
            </div>
            <div className="w-[50%]">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                PMRR
              </label>
              <SearchableDropdown
                fetchFunction={(param) =>
                  fetchAllProcurementMRRs({ ...param, requesting_warehouse: warehouseId, })
                }
                onSelect={setPMRRId}
                value={pmrrId}
                valueKey="id"
                labelKey="mrr_code"
                placeholder={pmrr ? pmrr : "Select PMRR"}
                disabled={!warehouseId}
              />
            </div>
          </div>
          {/* Notes Field */}
          <div>
            <label className="block text-gray-700 font-medium md:text-sm text-xs my-1">
              Notes <sub className="text-red-500">*</sub>
            </label>
            <textarea
              rows="2"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Write any notes about this return..."
              className="w-full border border-gray-300 rounded-lg px-3 py-1 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
              disabled={mode === STOCK_MODAL_MODES.VIEW}
            ></textarea>
          </div>
          <div className="flex items-start justify-between gap-4">
            {/* Status Section (Fixed Position) */}
            {mode !== STOCK_MODAL_MODES.ADD && status && (
              <div className="flex flex-col mt-2">
                <label className="block text-gray-700  font-medium md:text-sm text-xs mb-1">
                  Status
                </label>
                <span
                  className={`py-1.5 px-3 text-sm md:font-medium rounded-lg ${getPGRNStatusColor(
                    status
                  )}`}
                >
                  {toProperCase(status)}
                </span>
              </div>
            )}

            {/* Final Invoice Section */}
            {mode === STOCK_MODAL_MODES.VIEW && (
              <div className="w-[60%]">
                {finalInvoiceUrl ? (
                  <div>
                    <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
                      Final Invoice <sub className="text-red-500">*</sub>
                    </label>
                    <div className="flex items-center justify-between bg-gray-100 px-3 py-1 rounded-lg border">
                      <a
                        href={finalInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm truncate max-w-[350px]"
                      >
                        {extractFileName(finalInvoiceUrl)}
                      </a>
                      <div className="flex items-center gap-1">
                        {user.role === ROLES.PROCUREMENT_INCHARGE &&
                          status ===
                            PGRN_STATUS.PENDING_PROCUREMENT_APPROVAL && (
                            <button
                              onClick={() => handleDelete(finalInvoiceUrl)}
                              className="text-red-500 text-xs px-1 py-1"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          )}

                        <button
                          onClick={() => handleDownload(finalInvoiceUrl)}
                          className="text-primary text-xs px-1 py-1"
                        >
                          <FiDownload size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : user.role === ROLES.PROCUREMENT_INCHARGE &&
                  status === PGRN_STATUS.PENDING_PROCUREMENT_APPROVAL ? (
                  <>
                    {
                      <div>
                        <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
                          Final Invoice <sub className="text-red-500">*</sub>
                        </label>
                        <input
                          type="file"
                          accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.zip"
                          onChange={(e) =>
                            setFinalInvoiceFile(e.target.files[0])
                          }
                          className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                        />
                        {finalInvoiceFile && (
                          <>
                            <RoleValidator
                              allowedRoles={ROLES.PROCUREMENT_INCHARGE}
                            >
                              <button
                                onClick={handleUploadFile}
                                className="mt-2 bg-primary text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700"
                              >
                                Upload Invoice
                              </button>
                            </RoleValidator>
                          </>
                        )}
                      </div>
                    }
                  </>
                ) : (
                  <div>
                    <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
                      Final Invoice <sub className="text-red-500">*</sub>
                    </label>
                    <span className="text-gray-500 text-sm">
                      File not uploaded
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side — Selected Materials */}
        <div>
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center text-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            Selected Materials
          </h3>
          {!(mode === STOCK_MODAL_MODES.VIEW) && (
            <>
              <label className="block text-gray-700 font-medium text-xs md:text-sm mb-1">
                Add Product <sub className="text-red-500">*</sub>
              </label>
               <ProductCategoryFilter
                  onProductSelect={handleAddMaterial}
                />
              <label className="block text-gray-700 font-medium text-xs md:text-sm my-2">
                Selected Product List
              </label>
            </>
          )}

          {materials.length === 0 ? (
            <div className="text-gray-600 text-center py-11 border rounded-lg">
              No materials selected yet.
            </div>
          ) : (
            <div
              className={`overflow-x-auto ${
                mode === STOCK_MODAL_MODES.ADD ? "h-[26vh]" : "h-[23vh]"
              } md:h-[40vh] overflow-y-auto`}
            >
              {loading ? (
                <div className="h-full w-full flex justify-center items-center">
                  <div className=" animate-spin border-t border-blue-600"></div>
                </div>
              ) : (
                <GenericTable
                  tableColumns={
                    mode === STOCK_MODAL_MODES.VIEW
                      ? MATERIAL_COLUMNS_PGRN.filter((col) => col !== "actions")
                      : MATERIAL_COLUMNS_PGRN
                  }
                  tableRows={materials}
                  columnLabels={MATERIAL_LABELS_PGRN}
                  renderField={renderField}
                  enableMobileCardView={false}
                />
              )}
            </div>
          )}

          {/* Action Buttons Section */}
          {/* {mode !== STOCK_MODAL_MODES.VIEW && (
            <div className="flex justify-end gap-4 mt-1">
              <RoleValidator
                allowedRoles={[ROLES.INVENTORY_INCHARGE, ROLES.INVENTORY_ADMIN]}
              >
                <button
                  onClick={() => handleSubmit(PGRN_STATUS.DRAFT)}
                  className="w-full h-7 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-lg"
                >
                  Save as Draft
                </button>
              </RoleValidator>

              <RoleValidator
                allowedRoles={[ROLES.INVENTORY_INCHARGE, ROLES.INVENTORY_ADMIN]}
              >
                <button
                  onClick={() => handleSubmit(PGRN_STATUS.PENDING_APPROVAL)}
                  className="w-full h-7 bg-primary hover:bg-blue-700 text-white rounded-lg"
                >
                  Submit for Approval
                </button>
              </RoleValidator>
            </div>
          )} */}
          {/* Action Buttons Section */}
          <div className="mt-3">
            {((status === PGRN_STATUS.DRAFT &&
              mode === STOCK_MODAL_MODES.EDIT) ||
              mode === STOCK_MODAL_MODES.ADD) && (
              <InchargeSubmitButtons handleSubmit={handleSubmit} />
            )}

            {status === PGRN_STATUS.PENDING_APPROVAL &&
              mode === STOCK_MODAL_MODES.VIEW && (
                <AdminApprovalButtons handleSubmit={handleSubmit} />
              )}

            {status === PGRN_STATUS.APPROVED && (
              <SendForProcurementButton handleSubmit={handleSubmit} />
            )}

            {(status === PGRN_STATUS.PENDING_PROCUREMENT_APPROVAL ||
              status === PGRN_STATUS.BILLING_APPROVED) &&
              mode === STOCK_MODAL_MODES.VIEW && (
                <ProcurementActionButtons
                  handleSubmit={handleSubmit}
                  status={status}
                />
              )}

            {status === PGRN_STATUS.PENDING_BILLING_APPROVAL && (
              <BillingCompletionButtons handleSubmit={handleSubmit} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementGRNCreate;